import { type ElementRef, useEffect, useRef } from 'react'

import styled, { keyframes } from 'styled-components'

import { AudioPlayButton, AudioTag, AudioWave } from 'components-ref/atoms'
import {
    AudioContextProvider,
    useAudioStore
} from 'context/AudioContext/AudioContext'

interface IAudioMinimizedProps {
    mediaUrl: string
    processing: boolean
}

export const AudioInMessage = ({
    mediaUrl,
    processing
}: IAudioMinimizedProps) => {
    return (
        <AudioContextProvider audioReady={true} mediaUrl={mediaUrl}>
            <AudioWrapper
                processing={processing || !mediaUrl}
                mediaUrl={mediaUrl}
            />
        </AudioContextProvider>
    )
}

interface AudioWrapperProps {
    processing: boolean
    mediaUrl: string
}

const AudioWrapper = ({ processing, mediaUrl }: AudioWrapperProps) => {
    const { setMediaUrl } = useAudioStore()

    const audioRef = useRef<ElementRef<'audio'>>(null)

    useEffect(() => {
        setMediaUrl(mediaUrl)
    }, [mediaUrl])

    return (
        <>
            <AudioTag ref={audioRef} />
            <Wrapper>
                <WavePlayWrapper>
                    <AudioWaveContainer>
                        <AudioWaveWrapper>
                            <AudioWave
                                waveColor='white'
                                waveBackgroundColor={'blue'}
                                audioRef={audioRef?.current}
                                hasTimer={false}
                            />
                        </AudioWaveWrapper>
                    </AudioWaveContainer>

                    {processing ? (
                        <ProcessingWrapper>
                            <ProcessingFileLoader />
                        </ProcessingWrapper>
                    ) : (
                        <AudioPlayButton audioRef={audioRef} />
                    )}
                </WavePlayWrapper>
            </Wrapper>
        </>
    )
}

const Wrapper = styled.div`
    width: 100%;
    height: auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 1;
`

const WavePlayWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
`

const AudioWaveContainer = styled.div`
    width: 142px;
    height: 100%;
    position: relative;
    flex-shrink: 0;
    display: flex;
    flex: 1;
`

const AudioWaveWrapper = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    padding: 1rem;
    padding-right: 0;
`

const ProcessingWrapper = styled.div`
    height: 3rem;
    width: 3rem;
    margin-left: 1rem;
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
`

const mulShdSpin = keyframes`
    0%,
    100% {
        box-shadow:
            0 -3em 0 0.2em,
            2em -2em 0 0em,
            3em 0 0 -1em,
            2em 2em 0 -1em,
            0 3em 0 -1em,
            -2em 2em 0 -1em,
            -3em 0 0 -1em,
            -2em -2em 0 0;
    }
    12.5% {
        box-shadow:
            0 -3em 0 0,
            2em -2em 0 0.2em,
            3em 0 0 0,
            2em 2em 0 -1em,
            0 3em 0 -1em,
            -2em 2em 0 -1em,
            -3em 0 0 -1em,
            -2em -2em 0 -1em;
    }
    25% {
        box-shadow:
            0 -3em 0 -0.5em,
            2em -2em 0 0,
            3em 0 0 0.2em,
            2em 2em 0 0,
            0 3em 0 -1em,
            -2em 2em 0 -1em,
            -3em 0 0 -1em,
            -2em -2em 0 -1em;
    }
    37.5% {
        box-shadow:
            0 -3em 0 -1em,
            2em -2em 0 -1em,
            3em 0em 0 0,
            2em 2em 0 0.2em,
            0 3em 0 0em,
            -2em 2em 0 -1em,
            -3em 0em 0 -1em,
            -2em -2em 0 -1em;
    }
    50% {
        box-shadow:
            0 -3em 0 -1em,
            2em -2em 0 -1em,
            3em 0 0 -1em,
            2em 2em 0 0em,
            0 3em 0 0.2em,
            -2em 2em 0 0,
            -3em 0em 0 -1em,
            -2em -2em 0 -1em;
    }
    62.5% {
        box-shadow:
            0 -3em 0 -1em,
            2em -2em 0 -1em,
            3em 0 0 -1em,
            2em 2em 0 -1em,
            0 3em 0 0,
            -2em 2em 0 0.2em,
            -3em 0 0 0,
            -2em -2em 0 -1em;
    }
    75% {
        box-shadow:
            0em -3em 0 -1em,
            2em -2em 0 -1em,
            3em 0em 0 -1em,
            2em 2em 0 -1em,
            0 3em 0 -1em,
            -2em 2em 0 0,
            -3em 0em 0 0.2em,
            -2em -2em 0 0;
    }
    87.5% {
        box-shadow:
            0em -3em 0 0,
            2em -2em 0 -1em,
            3em 0 0 -1em,
            2em 2em 0 -1em,
            0 3em 0 -1em,
            -2em 2em 0 0,
            -3em 0em 0 0,
            -2em -2em 0 0.2em;
    }

`

const ProcessingFileLoader = styled.span`
    display: block;
    color: #000;
    font-size: 0.4rem;
    width: 1em;
    height: 1em;
    border-radius: 50%;
    position: relative;
    text-indent: -9999em;
    animation: ${mulShdSpin} 1.3s infinite linear;
    transform: translateZ(0);
`
